import { useCallback, useState } from "react";
import {
  TAB_DEFAULT_VIEW,
  type HomeTab,
  type HomeViewMode,
} from "../utils/homeBrowserTypes";

const storageKey = (tab: HomeTab, prefix: string) =>
  `home-browser-view-mode:${prefix}${tab}`;

function readStoredMode(tab: HomeTab, prefix: string): HomeViewMode {
  if (typeof window === "undefined") return TAB_DEFAULT_VIEW[tab];
  const stored = window.localStorage.getItem(storageKey(tab, prefix));
  return stored === "grid" || stored === "list"
    ? stored
    : TAB_DEFAULT_VIEW[tab];
}

/**
 * Per-user grid/list preference for the home browser, remembered per tab.
 *
 * Deliberately NOT the `workspace-*-view-mode` keys: home defaults differ
 * (chats=list, everything else=grid) and a home toggle shouldn't flip the
 * workspace detail sections. The workspace browser passes a `keyPrefix`
 * (e.g. "ws:") for the same reason in reverse — a detail toggle shouldn't
 * flip home's preference.
 */
export function useHomeViewMode(
  tab: HomeTab,
  keyPrefix = "",
): [HomeViewMode, (mode: HomeViewMode) => void] {
  const [modeByTab, setModeByTab] = useState<Record<HomeTab, HomeViewMode>>(
    () => ({
      recents: readStoredMode("recents", keyPrefix),
      canvases: readStoredMode("canvases", keyPrefix),
      chats: readStoredMode("chats", keyPrefix),
      starred: readStoredMode("starred", keyPrefix),
      shared: readStoredMode("shared", keyPrefix),
    }),
  );

  const setMode = useCallback(
    (next: HomeViewMode) => {
      setModeByTab((prev) => ({ ...prev, [tab]: next }));
      if (typeof window !== "undefined") {
        window.localStorage.setItem(storageKey(tab, keyPrefix), next);
      }
    },
    [tab, keyPrefix],
  );

  return [modeByTab[tab], setMode];
}
